import { Client, getCurrentHost, EventEmitter } from "./wts.client"

/**
 * Função que verifica se o broker (servidor central) está acessível.
 * Resolve `true` quando o servidor responde (mesmo 401/404 — significa que está no ar),
 * `false` quando há falha de conexão/timeout.
 *
 * O shell desktop (ex.: Tauri) pode injetar um pinger que use o transporte nativo
 * (sem CORS) via {@link ConnectivityService.setPinger}.
 */
export type Pinger = () => Promise<boolean>

export interface ConnectivityOptions {
    /** Intervalo entre checagens periódicas (ms). */
    intervalMs?: number
    /** Timeout de cada checagem (ms). */
    timeoutMs?: number
    /** Checagens consecutivas necessárias para confirmar uma mudança de estado (histerese). */
    confirmThreshold?: number
}

/**
 * Serviço de conectividade compartilhado. Faz health-check periódico do broker e expõe o
 * estado `available` (síncrono) + evento `onChange`. Materializa o `Client.onLine` injetando
 * um probe — assim os pontos do código que já testam `if (!Client.onLine)` passam a refletir
 * a disponibilidade real do servidor.
 *
 * A premissa do modo de contingência é que o BROKER está indisponível, não a rede inteira:
 * serviços de borda (cfeserver, deviceserver, adquirente) seguem operando, por isso o sinal
 * primário é o alcance do broker, e `navigator.onLine` é apenas complementar.
 *
 * Em produção quem DIRIGE a detecção é o `<millennium-browser>`: ele chama {@link checkNow}
 * a partir do próprio heartbeat (que já mantém a sessão viva no servidor), com cadência
 * adaptativa, e expõe `online`/`onlineChange`. O timer interno ({@link start}) existe apenas
 * para usos avulsos (testes, shells sem heartbeat) — não deve ser ligado em paralelo ao browser.
 */
export class ConnectivityService {
    private static _instance: ConnectivityService | undefined

    static get instance(): ConnectivityService {
        if (!this._instance) this._instance = new ConnectivityService()
        return this._instance
    }

    /** Disparado com o novo valor (boolean) sempre que `available` muda. */
    readonly onChange = new EventEmitter()

    private _available = true
    private _timer: ReturnType<typeof setInterval> | null = null
    private _intervalMs = 15000
    private _timeoutMs = 4000
    private _confirmThreshold = 2

    // Histerese: acumula checagens consecutivas no estado oposto antes de confirmar a troca,
    // evitando flapping em conexão instável.
    private _pendingState: boolean | null = null
    private _pendingCount = 0

    private _pinger: Pinger

    private constructor() {
        this._pinger = () => this._defaultPing()
        // Injeta o estado síncrono no Client, ativando os caminhos `if (!Client.onLine)`.
        Client.setConnectivityProbe(() => this._available)
    }

    /** Estado atual: `true` = broker acessível; `false` = em contingência. */
    get available(): boolean {
        return this._available
    }

    /**
     * `true` enquanto uma troca de estado está em confirmação (histerese em andamento).
     * O `<millennium-browser>` usa isto para encurtar a cadência do heartbeat durante transições,
     * acelerando a detecção sem abrir mão da proteção contra flapping.
     */
    get isTransitioning(): boolean {
        return this._pendingState !== null
    }

    /** Substitui o mecanismo de ping (ex.: transporte nativo do shell Tauri). */
    setPinger(pinger: Pinger) {
        this._pinger = pinger
    }

    /**
     * Define o estado inicial como OFFLINE sem disparar `onChange` (é uma assunção, não uma
     * transição observada). Usado pelo `<millennium-browser>` no boot de contingência: assume-se
     * que o broker está fora (motivo de abrir a ferramenta) até o heartbeat CONFIRMAR o retorno
     * (com a histerese normal), evitando indicar "disponível" indevidamente nos primeiros segundos.
     */
    assumeOffline() {
        this._available = false
        this._pendingState = null
        this._pendingCount = 0
    }

    configure(opts: ConnectivityOptions) {
        if (opts.intervalMs !== undefined) this._intervalMs = opts.intervalMs
        if (opts.timeoutMs !== undefined) this._timeoutMs = opts.timeoutMs
        if (opts.confirmThreshold !== undefined) this._confirmThreshold = Math.max(1, opts.confirmThreshold)
    }

    /** Inicia o monitoramento periódico (idempotente). */
    start() {
        if (this._timer) return
        void this.checkNow()
        this._timer = setInterval(() => void this.checkNow(), this._intervalMs)
    }

    stop() {
        if (this._timer) {
            clearInterval(this._timer)
            this._timer = null
        }
    }

    /** Executa uma checagem imediata e retorna o estado resultante (já com histerese aplicada). */
    async checkNow(): Promise<boolean> {
        let reachable: boolean
        try {
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                reachable = false
            } else {
                reachable = await this._pinger()
            }
        } catch {
            reachable = false
        }
        this._applyWithHysteresis(reachable)
        return this._available
    }

    private _applyWithHysteresis(reachable: boolean) {
        if (reachable === this._available) {
            this._pendingState = null
            this._pendingCount = 0
            return
        }
        if (this._pendingState === reachable) {
            this._pendingCount++
        } else {
            this._pendingState = reachable
            this._pendingCount = 1
        }
        if (this._pendingCount >= this._confirmThreshold) {
            this._available = reachable
            this._pendingState = null
            this._pendingCount = 0
            this.onChange.trigger(this._available)
        }
    }

    private async _defaultPing(): Promise<boolean> {
        const host = getCurrentHost()
        const controller = new AbortController()
        const timer = setTimeout(() => controller.abort(), this._timeoutMs)
        try {
            // Qualquer resposta HTTP do host (inclusive 401/404) indica broker no ar.
            // credentials:"omit" é OBRIGATÓRIO: a raiz /api/ responde 401 + WWW-Authenticate
            // (negociação de auth do broker). Sem omitir credenciais, o fetch same-origin usa
            // includeCredentials=true e o navegador abre seu diálogo NATIVO de usuário/senha a
            // cada ping (a cada 15s enquanto sem sessão/login) — o popup recorrente reclamado.
            // Como só interessa se o host respondeu, omitir credenciais não muda a detecção.
            await fetch(host + "/api/", { method: "GET", cache: "no-store", credentials: "omit", signal: controller.signal })
            return true
        } catch {
            return false
        } finally {
            clearTimeout(timer)
        }
    }
}
